import queryClient from '@src/app/providers/queryClient';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { buildSectionCommitFormData } from '@src/shared/lib';
import { useMutation } from '@src/shared/query';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  clientAddressSearchResultFromDto,
  clientAddressSectionId,
  clientAddressUsageFromDto,
  clientAddressUsageUpdateToDto,
  createClientAddressPayloadToDto,
} from '../../lib';
import type {
  ClientAddressPeriod,
  ClientAddressSearchParams,
  ClientAddressSearchResult,
  ClientAddressSearchResultDto,
  ClientAddressUsage,
  ClientAddressUsageDto,
  ClientAddressUsageUpdate,
  CommitClientDetailResponse,
  CreateClientAddressPayload,
} from '../../types';
import { clientAddressSearchUrl, clientUrl } from './constants';
import { CLIENTS_KEY, clientDetailKey, invalidateClients } from './keys';

type UseClientAddressUsagesOptions = {
  period?: ClientAddressPeriod;
  enabled?: boolean;
};

/**
 * Addresses of a client: the current ones (Addresses section, also to
 * choose an office address) or the former ones.
 */
export function useClientAddressUsages(
  clientId: number | string,
  { period = 'current', enabled = true }: UseClientAddressUsagesOptions = {},
) {
  const apiClient = useAPIClient();
  return useQuery({
    queryKey: [...clientDetailKey(clientId), 'address-usages', period],
    enabled,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: `${clientUrl(clientId)}address-usages/`,
        payload: period === 'former' ? { period } : {},
        options: { signal },
      }) as Promise<ClientAddressUsageDto[]>,
    select: (data): ClientAddressUsage[] => data.map(clientAddressUsageFromDto),
  });
}

function searchPayload(params: ClientAddressSearchParams) {
  const payload: Record<string, string> = {};
  if (params.country !== undefined) payload.country = String(params.country);
  if (params.group !== undefined) payload.group = String(params.group);
  for (const field of ['city', 'postcode', 'lines'] as const) {
    const value = params[field]?.trim();
    if (value) payload[field] = value;
  }
  return payload;
}

/**
 * Up to 20 client addresses of both segments matching what has been typed
 * so far, best first ("Select Existing Address"). Runs only once a city,
 * postcode or address line is typed; debounce the typed values.
 */
export function useClientAddressSearch(
  params: ClientAddressSearchParams,
  { enabled = true }: { enabled?: boolean } = {},
) {
  const apiClient = useAPIClient();
  const payload = searchPayload(params);
  const hasText = Boolean(payload.city || payload.postcode || payload.lines);
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'address-search', payload],
    enabled: enabled && hasText,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      apiClient.request({
        url: clientAddressSearchUrl,
        payload,
        options: { signal },
      }) as Promise<ClientAddressSearchResultDto[]>,
    select: (data): ClientAddressSearchResult[] =>
      data.map(clientAddressSearchResultFromDto),
  });
}

/**
 * Create Address and Select Address panels: creates a new address or
 * links an existing one, saved at once. The client page and the lists
 * are refreshed; an exact duplicate fails with `duplicate_address` (see
 * `duplicateAddressIdFromError`).
 */
export function useCreateClientAddress() {
  return useMutation<
    ClientAddressUsage,
    Error,
    { clientId: number | string; payload: CreateClientAddressPayload }
  >({
    mutationKey: [...CLIENTS_KEY, 'addresses', 'create'],
    mutateFn: async ({ apiClient, clientId, payload }) =>
      clientAddressUsageFromDto(
        (await apiClient.request({
          url: `${clientUrl(clientId)}addresses/`,
          method: 'POST',
          payload: createClientAddressPayloadToDto(payload),
        })) as ClientAddressUsageDto,
      ),
    onSuccess: () => {
      void invalidateClients();
    },
  });
}

/**
 * Edit Address panel (and a single head-office switch): saves one usage
 * at once through the client commit, in the section of its period. Errors
 * of the row: `clientAddressUsageRowErrors(error, period)`.
 */
export function useUpdateClientAddressUsage() {
  return useMutation<
    CommitClientDetailResponse,
    Error,
    {
      clientId: number | string;
      usageId: number;
      period: ClientAddressPeriod;
      update: ClientAddressUsageUpdate;
    }
  >({
    mutationKey: [...CLIENTS_KEY, 'addresses', 'update'],
    mutateFn: ({ apiClient, clientId, usageId, period, update }) =>
      apiClient.request({
        url: `${clientUrl(clientId)}detail/commit/`,
        method: 'PATCH',
        payload: buildSectionCommitFormData({
          [clientAddressSectionId(period)]: {
            usages: [clientAddressUsageUpdateToDto(usageId, update)],
          },
        }),
        contentType: 'multipart/form-data',
      }) as Promise<CommitClientDetailResponse>,
    onSuccess: (response, variables) => {
      const saved = Object.values(response.results).find(Boolean);
      if (saved) {
        queryClient.setQueryData(clientDetailKey(variables.clientId), saved);
      }
      void invalidateClients();
    },
  });
}
